import { useState, type SyntheticEvent } from "react";
import * as authApi from "../auth/api";
import { errorKey } from "./errors";
import { useParentT, type ParentStringKey } from "./strings";

// A fresh parent session for parent mode, with the existing email-code sign-in
// (Better Auth email OTP, ADR-0018). The code is never stored.

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SignIn(props: {
  expired: boolean;
  /** Why the email code is needed, e.g. the device forgot the parent PIN. */
  notice?: ParentStringKey;
  onSignedIn: () => void;
}) {
  const t = useParentT();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<ParentStringKey | undefined>(
    props.notice ?? (props.expired ? "signin.expired" : undefined),
  );

  const send = async (event?: SyntheticEvent) => {
    event?.preventDefault();
    if (busy) return;
    const address = email.trim();
    if (!EMAIL.test(address)) {
      setMessage("signin.invalidEmail");
      return;
    }
    setBusy(true);
    const res = await authApi.sendCode(address);
    setBusy(false);
    if (!res.ok) {
      setMessage(errorKey(res.error));
      return;
    }
    setMessage(undefined);
    setStep("code");
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    const res = await authApi.signInWithCode(email.trim(), code.trim());
    setBusy(false);
    if (res.ok) {
      setCode("");
      props.onSignedIn();
      return;
    }
    if (res.error.kind !== "http") {
      setMessage(errorKey(res.error));
      return;
    }
    const expired = res.error.code === "OTP_EXPIRED" || res.error.code === "TOO_MANY_ATTEMPTS";
    setMessage(expired ? "signin.codeExpired" : "signin.wrongCode");
  };

  return (
    <section className="parent-panel" aria-labelledby="parent-signin-title">
      <h2 id="parent-signin-title" className="parent-heading">
        {t("signin.title")}
      </h2>
      {step === "email" ? (
        <form className="parent-form" onSubmit={(e) => void send(e)} noValidate>
          <p className="parent-muted">{t("signin.body")}</p>
          <label className="parent-label">
            {t("signin.emailLabel")}
            <input
              className="parent-input"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
            />
          </label>
          {message === undefined ? null : (
            <p className="parent-message" role="alert">
              {t(message)}
            </p>
          )}
          <button className="parent-button parent-button-primary" type="submit" disabled={busy}>
            {t("signin.sendCode")}
          </button>
        </form>
      ) : (
        <form className="parent-form" onSubmit={(e) => void submit(e)} noValidate>
          <p className="parent-muted">{t("signin.codeBody", { email: email.trim() })}</p>
          <label className="parent-label">
            {t("signin.codeLabel")}
            <input
              className="parent-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, ""));
              }}
            />
          </label>
          {message === undefined ? null : (
            <p className="parent-message" role="alert">
              {t(message)}
            </p>
          )}
          <button
            className="parent-button parent-button-primary"
            type="submit"
            disabled={busy || code.length !== 6}
          >
            {t("signin.submit")}
          </button>
          <button className="parent-link" type="button" disabled={busy} onClick={() => void send()}>
            {t("signin.resend")}
          </button>
        </form>
      )}
    </section>
  );
}
